import { supabase } from '../lib/supabase';
import MediaService from './mediaService';

// ImageUploadService - Platform-specific image upload handling
class ImageUploadService {
  /**
   * Upload image to LinkedIn using Images API via Supabase Edge Function
   * @param {File} file - Processed image file
   * @param {string} accessToken - LinkedIn access token
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, imageUrn?: string, error?: string}>}
   */
  static async uploadToLinkedIn(file, accessToken, userId) {
    try {
      // Get authenticated session
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session?.access_token) {
        throw new Error('User session not found');
      }

      // Prepare form data (userId from JWT, accessToken fetched server-side from DB)
      const formData = new FormData();
      formData.append('image', file);
      formData.append('platform', 'linkedin');

      // Call Supabase Edge Function for LinkedIn upload
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/media-uploader`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: formData
        }
      );

      const result = await response?.json();

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to upload image to LinkedIn');
      }

      return {
        success: true,
        imageUrn: result?.imageUrn,
        uploadUrl: result?.uploadUrl // For reference
      };

    } catch (error) {
      console.error('[ImageUploadService] LinkedIn upload failed:', error);
      return {
        success: false,
        error: error?.message || 'LinkedIn image upload failed'
      };
    }
  }

  /**
   * Upload image to Twitter/X using v2 Media API via Supabase Edge Function
   * @param {File} file - Processed image file  
   * @param {string} accessToken - Twitter access token
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, mediaId?: string, error?: string}>}
   */
  static async uploadToTwitter(file, accessToken, userId) {
    try {
      // Get authenticated session
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session?.access_token) {
        throw new Error('User session not found');
      }

      // Prepare form data (userId from JWT, accessToken fetched server-side from DB)
      const formData = new FormData();
      formData.append('image', file);
      formData.append('platform', 'twitter');

      // Call Supabase Edge Function for Twitter upload
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/media-uploader`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: formData
        }
      );

      const result = await response?.json();

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to upload image to Twitter/X');
      }

      return {
        success: true,
        mediaId: result?.mediaId
      };

    } catch (error) {
      console.error('[ImageUploadService] Twitter/X upload failed:', error);
      return {
        success: false,
        error: error?.message || 'Twitter/X image upload failed'
      };
    }
  }

  /**
   * Upload image to specific platform with preprocessing
   * @param {File} originalFile - Original image file
   * @param {string} platform - Target platform ('linkedin' or 'twitter')
   * @param {string} accessToken - Platform access token
   * @param {string} userId - User ID
   * @param {string} contentPostId - Optional content post ID for tracking
   * @returns {Promise<{success: boolean, result?: any, error?: string, warnings?: string[]}>}
   */
  static async uploadToPlatform(originalFile, platform, accessToken, userId, contentPostId = null) {
    try {
      // Step 1: Optimize image for platform
      const optimization = await MediaService.optimizeForPlatform(originalFile, platform);
      
      if (!optimization.success) {
        return {
          success: false,
          error: `Image optimization failed: ${optimization.errors.join(', ')}`,
          warnings: optimization.warnings
        };
      }

      // Step 2: Upload to platform
      let uploadResult;
      
      if (platform === 'linkedin') {
        uploadResult = await this.uploadToLinkedIn(optimization.file, accessToken, userId);
      } else if (platform === 'twitter') {
        uploadResult = await this.uploadToTwitter(optimization.file, accessToken, userId);
      } else {
        return {
          success: false,
          error: `Unsupported platform: ${platform}`
        };
      }

      if (!uploadResult.success) {
        return {
          success: false,
          error: uploadResult.error,
          warnings: optimization.warnings
        };
      }

      // Step 3: Store upload reference in database (skipped — content_images table not yet created)
      // TODO: Create content_images table and re-enable when image tracking feature is built

      return {
        success: true,
        result: uploadResult,
        warnings: optimization.warnings
      };

    } catch (error) {
      console.error(`[ImageUploadService] Upload to ${platform} failed:`, error);
      return {
        success: false,
        error: error?.message || `Failed to upload to ${platform}`
      };
    }
  }

  /**
   * Upload multiple images to a platform
   * @param {File[]} files - Array of image files
   * @param {string} platform - Target platform
   * @param {string} accessToken - Platform access token
   * @param {string} userId - User ID
   * @param {string} contentPostId - Optional content post ID
   * @returns {Promise<{successful: any[], failed: any[], warnings: string[]}>}
   */
  static async uploadMultipleToPlatform(files, platform, accessToken, userId, contentPostId = null) {
    const successful = [];
    const failed = [];
    const allWarnings = [];

    // Check platform limits
    const platformRules = MediaService.PLATFORM_RULES[platform];
    if (files.length > platformRules?.maxImages) {
      return {
        successful: [],
        failed: files.map(file => ({
          file,
          error: `${platform} supports maximum ${platformRules.maxImages} images. You selected ${files.length}.`
        })),
        warnings: []
      };
    }

    // Upload images concurrently (but respect rate limits)
    const uploadPromises = files.map(async (file, index) => {
      // Add small delay between uploads to avoid rate limiting
      if (index > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const result = await this.uploadToPlatform(file, platform, accessToken, userId, contentPostId);
      
      if (result.success) {
        successful.push({
          file,
          result: result.result,
          warnings: result.warnings || []
        });
      } else {
        failed.push({
          file,
          error: result.error,
          warnings: result.warnings || []
        });
      }

      // Collect warnings
      if (result.warnings?.length > 0) {
        allWarnings.push(...result.warnings);
      }
    });

    await Promise.all(uploadPromises);

    return {
      successful,
      failed,
      warnings: [...new Set(allWarnings)] // Remove duplicates
    };
  }

  /**
   * Store image upload reference in database
   * @private
   * @param {string} userId
   * @param {string} contentPostId  
   * @param {string} platform
   * @param {string} imageUrn - LinkedIn URN
   * @param {string} mediaId - Twitter media ID
   * @param {string} originalFilename
   * @param {number} fileSize
   * @param {string} mimeType
   */
  static async storeImageReference(userId, contentPostId, platform, imageUrn, mediaId, originalFilename, fileSize, mimeType) {
    const { error } = await supabase
      ?.from('content_images')
      ?.insert([{
        user_id: userId,
        content_post_id: contentPostId,
        platform: platform,
        image_urn: imageUrn,
        media_id: mediaId,
        original_filename: originalFilename,
        file_size: fileSize,
        mime_type: mimeType,
        upload_status: 'uploaded'
      }]);

    if (error) {
      // Non-critical — image was already uploaded to the platform successfully.
      // The content_images table may not exist yet; log and continue.
      console.warn('[ImageUploadService] Failed to store image reference (non-critical):', error?.message);
    }

  }

  /**
   * Get uploaded images for content post
   * @param {string} contentPostId
   * @param {string} platform - Optional platform filter
   * @returns {Promise<any[]>}
   */
  static async getUploadedImages(contentPostId, platform = null) {
    let query = supabase
      ?.from('content_images')
      ?.select('*')
      ?.eq('content_post_id', contentPostId)
      ?.eq('upload_status', 'uploaded');

    if (platform) {
      query = query?.eq('platform', platform);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[ImageUploadService] Failed to get uploaded images:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Delete uploaded image reference
   * @param {string} imageId - Image record ID
   * @returns {Promise<boolean>}
   */
  static async deleteImageReference(imageId) {
    const { error } = await supabase
      ?.from('content_images')
      ?.delete()
      ?.eq('id', imageId);

    if (error) {
      console.error('[ImageUploadService] Failed to delete image reference:', error);
      return false;
    }

    return true;
  }

  /**
   * Validate upload prerequisites (tokens, connectivity)
   * @param {string} platform
   * @param {string} accessToken
   * @returns {Promise<{valid: boolean, error?: string}>}
   */
  static async validateUploadPrerequisites(platform, accessToken) {
    try {
      if (!accessToken) {
        return {
          valid: false,
          error: `No ${platform} access token provided`
        };
      }

      // Check if Supabase is accessible
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        return {
          valid: false,
          error: 'User session not found'
        };
      }

      // TODO: Add token validation calls to each platform
      // For now, assume valid if token exists
      return { valid: true };

    } catch (error) {
      return {
        valid: false,
        error: `Upload prerequisites validation failed: ${error.message}`
      };
    }
  }
}

export default ImageUploadService;